import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaPaperPlane, FaSmile, FaUsers, FaComments, FaWifi, FaExclamationTriangle } from 'react-icons/fa';
import { useAuth } from '../../hooks/useApi';
import { theme, animations } from '../../styles/theme';
import chatService from '../../services/ChatService';
import ApiService from '../../services/ApiService';

const MEDIA_BASE_URL = (
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_URL ||
  ''
).replace(/\/$/, '');

const resolveAvatarUrl = (url) => {
  if (!url) return null;
  const s = String(url).trim();
  if (!s) return null;
  if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('data:') || s.startsWith('blob:')) return s;
  if (!MEDIA_BASE_URL) return s;
  return `${MEDIA_BASE_URL}${s.startsWith('/') ? '' : '/'}${s}`;
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const messageSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const ChatContainer = styled.div`
  height:  calc(100vh - 80px);
  width: 100vw;
  background: ${theme.gradients.background};
  position: fixed;
  top: 80px;
  left: 0;
  overflow: hidden;
  box-sizing: border-box;
&::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 20% 80%, rgba(0, 0, 0, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 80% 20%, rgba(0, 0, 0, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 40% 40%, rgba(0, 0, 0, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 2% 4%, rgba(255, 255, 255, 0.26) 1px, transparent 0),
                      radial-gradient(circle at 29% 36%, rgba(255, 255, 255, 0.32) 1px, transparent 0),
                      radial-gradient(circle at 50% 60%, rgba(255, 255, 255, 0.2) 1px, transparent 0),
                      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.66) 1px, transparent 0),
                      radial-gradient(circle at 70% 10%, rgba(255, 255, 255, 0.11) 1px, transparent 0),
                      radial-gradient(circle at 60% 12%, rgba(255, 255, 255, 0.29) 1px, transparent 0),
                      radial-gradient(circle at 43% 90%, rgba(255, 255, 255, 0.51) 1px, transparent 0),
                      radial-gradient(circle at 32% 83%, rgba(255, 255, 255, 0.16) 1px, transparent 0),
                      radial-gradient(circle at 49% 53%, rgba(255, 255, 255, 0.6) 1px, transparent 0),
                      radial-gradient(circle at 89% 52%, rgba(255, 255, 255, 0.14) 1px, transparent 0);

    
    background-size: 25% 25%;
    pointer-events: none;
    background-repeat: repeat;
    animation: ${animations.sparkle} 4s ease-in-out infinite;

  }
`;

const ChatContent = styled.div`
  max-width: 1400px;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  padding: ${theme.spacing.md};
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  gap: 0;
  
  @media (max-width: 1024px) {
    padding: ${theme.spacing.sm};
  }
  
  @media (max-width: 768px) {
    padding: ${theme.spacing.xs};
  }
  
  @media (max-width: 480px) {
    padding: 4px;
  }
`;

const ChatHeader = styled.div`
  background: ${theme.colors.cardBackground};
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.borderRadius.large} ${theme.borderRadius.large} 0 0;
  box-shadow: ${theme.shadows.card};
  backdrop-filter: blur(10px);
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
  animation: ${fadeIn} 0.6s ease-out;
  box-sizing: border-box;
  width: 100%;
  flex-shrink: 0;
  gap: ${theme.spacing.sm};
  min-height: 60px;
  
  @media (max-width: 768px) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    border-radius: ${theme.borderRadius.medium} ${theme.borderRadius.medium} 0 0;
    min-height: 50px;
    flex-wrap: wrap;
  }
  
  @media (max-width: 480px) {
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    flex-direction: row;
    min-height: 45px;
  }
`;

const ChatTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const ChatTitleText = styled.h1`
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSizes.lg};
  margin: 0;
  background: ${theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: ${theme.typography.fontSizes.md};
  }
`;

const OnlineCount = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  background: ${theme.colors.success};
  color: #000;
  padding: 6px 12px;
  border-radius: ${theme.borderRadius.large};
  font-size: ${theme.typography.fontSizes.xs};
  font-weight: ${theme.typography.fontWeights.semibold};
  white-space: nowrap;
  
  @media (max-width: 480px) {
    padding: 4px 8px;
  }
`;

const ChatControls = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
`;

const ControlButton = styled.button`
  background: ${theme.colors.cardBackground};
  border: 1px solid ${theme.colors.cardBorder};
  color: ${theme.colors.textSecondary};
  padding: 8px;
  border-radius: ${theme.borderRadius.medium};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  
  &:hover {
    color: ${theme.colors.primary};
    border-color: ${theme.colors.primary};
    transform: translateY(-2px);
  }
  
  &.active {
    background: ${theme.gradients.primary};
    color: #000;
    border-color: ${theme.colors.primary};
  }
  
  @media (max-width: 480px) {
    padding: 6px;
    min-width: 32px;
    height: 32px;
  }
`;

const ChatMessages = styled.div`
  flex: 1;
  min-height: 0;
  background: ${theme.colors.cardBackground};
  border-left: 1px solid ${theme.colors.cardBorder};
  border-right: 1px solid ${theme.colors.cardBorder};
  backdrop-filter: blur(10px);
  padding: ${theme.spacing.md};
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.primary};
    border-radius: 4px;
    
    &:hover {
      background: ${theme.colors.primaryDark};
    }
  }
  
  @media (max-width: 768px) {
    padding: ${theme.spacing.sm};
    gap: ${theme.spacing.xs};
  }
`;

const Message = styled.div`
  display: flex;
  gap: 8px;
  animation: ${messageSlideIn} 0.3s ease-out;
  padding: 2px 16px;
  will-change: transform;
  align-items: flex-start;

  &.own-message {
    flex-direction: row-reverse;
  }

  @media (max-width: 768px) {
    padding: 2px 8px;
    gap: 6px;
  }
`;

const AvatarWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
  margin-top: 6px;
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  background: ${props => props.$hasImage ? 'transparent' : theme.gradients.primary};
  border-radius: ${theme.borderRadius.round};
  border: 2px solid ${props => props.$frameColor || 'rgba(255,255,255,0.1)'};
  box-shadow: ${props => props.$frameColor ? `0 0 6px ${props.$frameColor}44` : 'none'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${theme.typography.fontWeights.bold};
  font-size: 12px;
  color: #000;
  flex-shrink: 0;
  overflow: hidden;
  transition: border-color 0.3s ease;

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
    font-size: 11px;
  }
`;

const ChatBadge = styled.div`
  position: absolute;
  bottom: -2px;
  right: -4px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${theme.gradients.primary};
  border: 1.5px solid ${theme.colors.cardBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 50%;
  }
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: ${theme.borderRadius.round};
  &[src=""], &:not([src]) {
    display: none;
  }
`;

const MessageContent = styled.div`
  max-width: 65%;
  min-width: 120px;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    max-width: 78%;
  }
`;

const MessageBubble = styled.div`
  background: ${props => props.$isOwn
    ? 'transparent'
    : theme.colors.cardBackground};
  border: 1px solid ${props => props.$isOwn
    ? 'transparent'
    : theme.colors.cardBorder};
  color: ${theme.colors.textPrimary};
  padding: 6px 10px 6px 10px;
  border-radius: ${props => props.$isOwn
    ? '10px 10px 2px 10px'
    : '10px 10px 10px 2px'};
  min-width: ${props => props.$isOwn ? '160px' : 'auto'};
  position: relative;
  word-wrap: break-word;
  overflow-wrap: break-word;

  @media (max-width: 768px) {
    min-width: ${props => props.$isOwn ? '130px' : 'auto'};
  }
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 2px;
`;

const Username = styled.span`
  color: ${theme.colors.primary};
  font-weight: ${theme.typography.fontWeights.semibold};
  font-size: 12.5px;
`;

const Timestamp = styled.span`
  color: ${theme.colors.textMuted};
  font-size: 10.5px;
  margin-left: auto;
  white-space: nowrap;
`;

const MessageText = styled.div`
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSizes.sm};
  line-height: 1.4;
  word-wrap: break-word;
`;

const ChatInputContainer = styled.div`
  background: ${theme.colors.cardBackground};
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: 0 0 ${theme.borderRadius.large} ${theme.borderRadius.large};
  box-shadow: ${theme.shadows.card};
  backdrop-filter: blur(10px);
  padding: ${theme.spacing.md};
  flex-shrink: 0;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: ${theme.spacing.sm};
    border-radius: 0 0 ${theme.borderRadius.medium} ${theme.borderRadius.medium};
  }
  
  @media (max-width: 480px) {
    padding: ${theme.spacing.xs};
  }
`;

const InputWrapper = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  align-items: flex-end;
  
  @media (max-width: 480px) {
    gap: ${theme.spacing.xs};
  }
`;

const MessageInput = styled.textarea`
  flex: 1;
  background: ${theme.colors.inputBackground};
  border: 1px solid ${theme.colors.inputBorder};
  border-radius: ${theme.borderRadius.medium};
  color: ${theme.colors.textPrimary};
  padding: 10px 12px;
  font-size: ${theme.typography.fontSizes.sm};
  font-family: inherit;
  resize: none;
  min-height: 20px;
  max-height: 80px;
  transition: all 0.3s ease;
  box-sizing: border-box;
  line-height: 1.4;

  &::placeholder {
    color: ${theme.colors.textDisabled};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    background: ${theme.colors.inputBackground};
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
  }
  
  @media (max-width: 480px) {
    padding: 8px 10px;
    font-size: ${theme.typography.fontSizes.xs};
  }
`;

const SendButton = styled.button`
  background: ${theme.gradients.primary};
  color: #000;
  border: none;
  padding: 10px;
  border-radius: ${theme.borderRadius.medium};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.buttonHover};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  @media (max-width: 480px) {
    width: 38px;
    height: 38px;
    padding: 8px;
  }
`;

const EmojiButton = styled.button`
  background: none;
  border: 1px solid ${theme.colors.cardBorder};
  color: ${theme.colors.textSecondary};
  padding: 10px;
  border-radius: ${theme.borderRadius.medium};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  position: relative;
  flex-shrink: 0;

  &:hover {
    color: ${theme.colors.primary};
    border-color: ${theme.colors.primary};
    transform: translateY(-2px);
  }

  &.active {
    background: ${theme.colors.primary};
    color: #000;
  }
  
  @media (max-width: 480px) {
    width: 38px;
    height: 38px;
    padding: 8px;
  }
`;

const EmojiPicker = styled.div`
  position: absolute;
  bottom: 60px;
  left: 0;
  background: ${theme.colors.cardBackgroundThick};
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.card};
  
  padding: ${theme.spacing.md};
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: ${theme.spacing.sm};
  width:auto;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.primary};
    border-radius: 3px;
  }
`;

const EmojiItem = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  padding: ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.small};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;

  &:hover {
    background: rgba(255, 215, 0, 0.2);
    transform: scale(1.1);
  }
`;

const SystemMessage = styled.div`
  text-align: center;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSizes.sm};
  font-style: italic;
  padding: ${theme.spacing.sm};
  background: rgba(255, 255, 255, 0.05);
  border-radius: ${theme.borderRadius.medium};
  margin: ${theme.spacing.sm} 0;
`;

const ConnectionStatus = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: 6px 10px;
  background: ${props => props.$connected ? 
    'rgba(81, 207, 102, 0.2)' : 'rgba(255, 107, 107, 0.2)'};
  border: 1px solid ${props => props.$connected ? 
    'rgba(81, 207, 102, 0.3)' : 'rgba(255, 107, 107, 0.3)'};
  border-radius: ${theme.borderRadius.medium};
  color: ${props => props.$connected ? theme.colors.success : theme.colors.error};
  font-size: ${theme.typography.fontSizes.xs};
  white-space: nowrap;
  
  @media (max-width: 480px) {
    padding: 4px 8px;
    font-size: 10px;
  }
`;

const TypingIndicator = styled.div`
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSizes.xs};
  font-style: italic;
  padding: ${theme.spacing.sm};
  animation: ${messageSlideIn} 0.3s ease-out;
`;

const ChatRoom = ({ onNavigate }) => {
  const { user, token } = useAuth();

  // Read latest user cosmetics from localStorage for own messages
  const getUserCosmetics = useCallback(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      return {
        avatarUrl: stored.avatarUrl || user?.avatarUrl || null,
        frameColor: stored.metadata?.frameColor || user?.metadata?.frameColor || null,
        badgeImageUrl: stored.metadata?.badgeImageUrl || user?.metadata?.badgeImageUrl || null,
      };
    } catch {
      return {
        avatarUrl: user?.avatarUrl || null,
        frameColor: user?.metadata?.frameColor || null,
        badgeImageUrl: user?.metadata?.badgeImageUrl || null,
      };
    }
  }, [user]);

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [onlineUsers, setOnlineUsers] = useState(0);
  const [soundEnabled] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [typingUsers, setTypingUsers] = useState([]);
  const [error, setError] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const roomId = 'public';

  // SessionStorage key for persisting messages
  const MESSAGES_STORAGE_KEY = `chat_messages_${roomId}`;

  // Load messages from sessionStorage on mount
  useEffect(() => {
    if (!user) return;
    
    try {
      const storedMessages = sessionStorage.getItem(MESSAGES_STORAGE_KEY);
      if (storedMessages) {
        const parsedMessages = JSON.parse(storedMessages);
        console.log('📦 Loaded messages from sessionStorage:', parsedMessages.length);
        setMessages(parsedMessages);
      }
    } catch (error) {
      console.error('Failed to load messages from sessionStorage:', error);
    }
  }, [user]);

  // Save messages to sessionStorage whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      try {
        sessionStorage.setItem(MESSAGES_STORAGE_KEY, JSON.stringify(messages));
        console.log('💾 Saved messages to sessionStorage:', messages.length);
      } catch (error) {
        console.error('Failed to save messages to sessionStorage:', error);
      }
    }
  }, [messages]);

  // Common emojis for quick access
  const commonEmojis = [
    '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇',
    '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚',
    '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩',
    '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣',
    '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬',
    '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗',
    '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯',
    '😦', '😧', '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐',
    '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕', '🤑', '🤠', '😈',
    '👍', '👎', '👌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈',
    '👉', '👆', '🖕', '👇', '☝️', '👋', '🤚', '🖐️', '✋', '🖖',
    '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✊', '👊', '🤛', '🤜',
    '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔',
    '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️',
    '✊', '✌️', '🤞', '🤟', '🤘', '🤙', '🔥', '⭐', '🌟', '✨',
    '🎉', '🎊', '🎈', '🎁', '🏆', '🥇', '🥈', '🥉', '🏅', '🎖️'
  ];

  // Load initial messages and setup WebSocket connection
  useEffect(() => {
    if (!user) return;

    const initializeChat = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Check for cached messages in sessionStorage
        let cachedMessages = [];
        try {
          const storedMessages = sessionStorage.getItem(MESSAGES_STORAGE_KEY);
          if (storedMessages) {
            cachedMessages = JSON.parse(storedMessages);
            console.log('📦 Found cached messages:', cachedMessages.length);
          }
        } catch (error) {
          console.error('Failed to load cached messages:', error);
        }

        // Load initial messages from API
        let apiMessages = [];
        try {
          const messagesResponse = await ApiService.getChatMessages(roomId, 1, 50);
          console.log('Messages response:', messagesResponse);
          
          if (messagesResponse && messagesResponse.data && Array.isArray(messagesResponse.data)) {
            apiMessages = messagesResponse.data.map(msg => ({
              id: msg.id,
              username: msg.user?.username || 'Unknown',
              avatarUrl: msg.user?.avatarUrl || null,
              frameColor: msg.user?.metadata?.frameColor || null,
              badgeImageUrl: msg.user?.metadata?.badgeImageUrl || null,
              message: msg.message || '',
              timestamp: new Date(msg.createdAt || Date.now()).toLocaleTimeString(),
              isOwn: msg.user?.id === user.id,
              isSystem: msg.type === 'system'
            }));
            console.log('📥 Loaded API messages:', apiMessages.length);
          }
        } catch (apiError) {
          console.warn('Failed to load chat history:', apiError);
        }

        // Merge cached and API messages, removing duplicates
        let mergedMessages = [];
        if (cachedMessages.length > 0 && apiMessages.length > 0) {
          // Create a map of API messages by ID
          const apiMessageMap = new Map(apiMessages.map(msg => [msg.id, msg]));
          
          // Add cached messages that aren't in API response
          cachedMessages.forEach(msg => {
            if (!apiMessageMap.has(msg.id)) {
              mergedMessages.push(msg);
            }
          });
          
          // Add all API messages
          mergedMessages = [...mergedMessages, ...apiMessages];
          
          // Sort by timestamp
          mergedMessages.sort((a, b) => {
            const timeA = new Date(`1970/01/01 ${a.timestamp}`);
            const timeB = new Date(`1970/01/01 ${b.timestamp}`);
            return timeA - timeB;
          });
          
          console.log('🔄 Merged messages:', mergedMessages.length);
        } else if (cachedMessages.length > 0) {
          mergedMessages = cachedMessages;
          console.log('🔄 Using cached messages:', mergedMessages.length);
        } else if (apiMessages.length > 0) {
          mergedMessages = apiMessages.reverse();
          console.log('🔄 Using API messages:', mergedMessages.length);
        } else {
          // Add a welcome message if no messages exist
          mergedMessages = [{
            id: 'welcome',
            username: 'System',
            message: 'Welcome to the public chat room! Start chatting below.',
            timestamp: new Date().toLocaleTimeString(),
            isOwn: false,
            isSystem: true
          }];
        }
        
        setMessages(mergedMessages);

        // Connect to WebSocket
        console.log('🔌 Connecting to WebSocket...', { userId: user.id });
        chatService.connect(null, user.id);
        
        // Wait a moment for connection, then join room
        setTimeout(() => {
          console.log('🏠 Joining room:', roomId);
          chatService.joinRoom(roomId);
        }, 1000);

        // Update user status to online
        await ApiService.updateUserStatus('online');

      } catch (error) {
        console.error('Failed to initialize chat:', error);
        setError('Failed to connect to chat. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    initializeChat();

    return () => {
      if (chatService.isConnected) {
        chatService.leaveRoom(roomId);
        chatService.disconnect();
      }
    };
  }, [user, token, roomId]);

  // WebSocket event listeners
  useEffect(() => {
    if (!user) {
      console.log('Waiting for user...', { user: !!user });
      return;
    }
    
    console.log('🎯 Setting up WebSocket event listeners...', { connected: chatService.isConnected });

    const handleNewMessage = (messageData) => {
      try {
        console.log('📨 New message received:', messageData);
        
        // Extract user info - backend now always sends user.username and user.id
        let username = 'Unknown';
        let userId = null;
        
        if (messageData.user) {
          username = messageData.user.username || 'Unknown';
          userId = messageData.user.id;
        }
        
        // System messages
        if (messageData.type === 'system') {
          username = 'System';
        }
        
        console.log('👤 User info:', { username, userId, type: messageData.type });
        
        const newMsg = {
          id: messageData.id || Date.now(),
          username: username,
          avatarUrl: messageData.user?.avatarUrl || null,
          frameColor: messageData.user?.metadata?.frameColor || null,
          badgeImageUrl: messageData.user?.metadata?.badgeImageUrl || null,
          message: messageData.message || '',
          timestamp: new Date(messageData.createdAt || Date.now()).toLocaleTimeString(),
          isOwn: userId === user?.id,
          isSystem: messageData.type === 'system'
        };
        
        console.log('✨ Adding message to state:', newMsg);
        setMessages(prev => {
          // Check if message already exists (prevent duplicates)
          const isDuplicate = prev.some(msg => 
            msg.id === newMsg.id || 
            (msg.message === newMsg.message && 
             msg.username === newMsg.username && 
             Math.abs(new Date(`1970/01/01 ${msg.timestamp}`) - new Date(`1970/01/01 ${newMsg.timestamp}`)) < 1000)
          );
          
          if (isDuplicate) {
            console.log('⚠️ Duplicate message detected, skipping:', newMsg.id);
            return prev;
          }
          
          const updated = [...prev, newMsg];
          console.log('📝 Updated messages count:', updated.length);
          return updated;
        });
        
        // Play sound notification if enabled
        if (soundEnabled && !newMsg.isOwn) {
          // Add sound notification here
          console.log('🔊 New message sound');
        }
      } catch (error) {
        console.error('Error handling new message:', error, messageData);
      }
    };

    const handleConnectionStatus = ({ connected }) => {
      setIsConnected(connected);
    };

    const handleOnlineCount = (count) => {
      setOnlineUsers(count);
    };

    const handleUserJoined = (userData) => {
      setMessages(prev => [...prev, {
        id: Date.now(),
        username: 'System',
        message: `${userData.username} joined the chat`,
        timestamp: new Date().toLocaleTimeString(),
        isOwn: false,
        isSystem: true
      }]);
    };

    const handleUserLeft = (userData) => {
      setMessages(prev => [...prev, {
        id: Date.now(),
        username: 'System',
        message: `${userData.username} left the chat`,
        timestamp: new Date().toLocaleTimeString(),
        isOwn: false,
        isSystem: true
      }]);
    };

    const handleTypingStart = ({ user: typingUser }) => {
      if (typingUser.id !== user?.id) {
        setTypingUsers(prev => {
          if (!prev.find(u => u.id === typingUser.id)) {
            return [...prev, typingUser];
          }
          return prev;
        });
      }
    };

    const handleTypingStop = ({ user: typingUser }) => {
      setTypingUsers(prev => prev.filter(u => u.id !== typingUser.id));
    };

    // Register event listeners
    console.log('🎧 Registering WebSocket event listeners...');
    chatService.on('new-message', handleNewMessage);
    chatService.on('connection-status', handleConnectionStatus);
    chatService.on('online-users-count', handleOnlineCount);
    chatService.on('user-joined', handleUserJoined);
    chatService.on('user-left', handleUserLeft);
    chatService.on('typing-start', handleTypingStart);
    chatService.on('typing-stop', handleTypingStop);
    
    // Log current connection status
    console.log('📊 Current connection status:', chatService.getConnectionStatus());

    return () => {
      // Cleanup event listeners
      chatService.off('new-message', handleNewMessage);
      chatService.off('connection-status', handleConnectionStatus);
      chatService.off('online-users-count', handleOnlineCount);
      chatService.off('user-joined', handleUserJoined);
      chatService.off('user-left', handleUserLeft);
      chatService.off('typing-start', handleTypingStart);
      chatService.off('typing-stop', handleTypingStop);
    };
  }, [user?.id, soundEnabled, user]); // Only depend on user.id, soundEnabled and user to prevent unnecessary re-registrations

  // Handle connection status changes and ensure listeners are active
  useEffect(() => {
    const checkAndSetupListeners = () => {
      if (chatService.isConnected && user) {
        console.log('🔗 Connection established, setting up listeners');
        // Force a re-render to setup listeners if they're not already set
        setIsConnected(true);
      }
    };

    // Check connection status periodically
    const connectionCheck = setInterval(checkAndSetupListeners, 1000);
    
    // Initial check
    checkAndSetupListeners();
    
    return () => clearInterval(connectionCheck);
  }, [user, soundEnabled]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = useCallback(async () => {
    if (newMessage.trim() === '' || !isConnected) {
      console.warn('Cannot send message:', { message: newMessage.trim(), connected: isConnected });
      return;
    }

    try {
      console.log('📤 Sending message:', { message: newMessage.trim(), roomId, user: user.username });
      
      // Send via WebSocket - server will save to database AND broadcast to all users
      const success = chatService.sendMessage(newMessage.trim(), roomId);
      
      if (success) {
        console.log('✅ Message sent via WebSocket (server will save and broadcast)');
        
        // Clear the input immediately for better UX
        setNewMessage('');
        chatService.stopTyping(roomId);
        
        // Note: We don't add the message to state here because:
        // 1. The server will broadcast it back to us via 'new-message' event
        // 2. This prevents duplicate messages
        // 3. Ensures message order is consistent across all clients
      } else {
        console.error('❌ WebSocket send failed - not connected');
        setError('Failed to send message. Check your connection.');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setError('Failed to send message. Please try again.');
    }
  }, [newMessage, isConnected, roomId, user]);

  const handleTyping = useCallback((value) => {
    setNewMessage(value);
    
    // Start typing indicator
    if (value.trim() && isConnected) {
      chatService.startTyping(roomId);
      
      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Stop typing after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        chatService.stopTyping(roomId);
      }, 2000);
    } else {
      chatService.stopTyping(roomId);
    }
  }, [isConnected, roomId]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleEmojiSelect = useCallback((emoji) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
    // Focus back on input after selecting emoji
    setTimeout(() => {
      const messageInput = document.querySelector('textarea');
      if (messageInput) {
        messageInput.focus();
      }
    }, 100);
  }, []);

  const toggleEmojiPicker = () => {
    setShowEmojiPicker(prev => !prev);
  };

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  const renderMessage = (msg) => {
    if (msg.isSystem) {
      return (
        <SystemMessage key={msg.id}>
          {msg.message}
        </SystemMessage>
      );
    }

    // Use current user's cosmetics for own messages, fallback to message data
    const ownCosmetics = msg.isOwn ? getUserCosmetics() : null;
    const msgAvatarUrl = msg.isOwn ? (ownCosmetics?.avatarUrl || msg.avatarUrl) : msg.avatarUrl;
    const msgFrameColor = msg.isOwn ? (ownCosmetics?.frameColor || msg.frameColor) : msg.frameColor;
    const msgBadgeImageUrl = msg.isOwn ? (ownCosmetics?.badgeImageUrl || msg.badgeImageUrl) : msg.badgeImageUrl;

    return (
      <Message key={msg.id} className={msg.isOwn ? 'own-message' : ''}>
        <AvatarWrapper>
          <UserAvatar $hasImage={!!msgAvatarUrl} $frameColor={msgFrameColor}>
            {msgAvatarUrl
              ? <AvatarImg
                  src={resolveAvatarUrl(msgAvatarUrl)}
                  alt={msg.username}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.textContent = msg.username.charAt(0).toUpperCase();
                  }}
                />
              : msg.username.charAt(0).toUpperCase()
            }
          </UserAvatar>
          {msgBadgeImageUrl && (
            <ChatBadge>
              <img
                src={resolveAvatarUrl(msgBadgeImageUrl)}
                alt="Badge"
                onError={(e) => { e.target.parentElement.style.display = 'none'; }}
              />
            </ChatBadge>
          )}
        </AvatarWrapper>
        <MessageContent>
          <MessageBubble $isOwn={msg.isOwn}>
            <MessageHeader>
              <Username>{msg.username}</Username>
              <Timestamp>{msg.timestamp}</Timestamp>
            </MessageHeader>
            <MessageText className="message-content">
              {msg.message}
            </MessageText>
          </MessageBubble>
        </MessageContent>
      </Message>
    );
  };

  return (
    <ChatContainer>
      <ChatContent>
        <ChatHeader>
          <ChatTitle>
            <FaComments size={24} color={theme.colors.primary} />
            <ChatTitleText>Public Chat Room</ChatTitleText>
          </ChatTitle>
          <ChatControls>
            <ConnectionStatus $connected={isConnected}>
              {isConnected ? <FaWifi /> : <FaExclamationTriangle />}
              {isConnected ? 'Connected' : 'Disconnected'}
            </ConnectionStatus>
            <OnlineCount>
              <FaUsers />
              {onlineUsers} Online
            </OnlineCount>
            {/* <ControlButton 
              className={soundEnabled ? 'active' : ''}
              onClick={() => setSoundEnabled(!soundEnabled)}
              title={soundEnabled ? 'Mute notifications' : 'Enable notifications'}
            >
              {soundEnabled ? <FaVolumeUp /> : <FaVolumeMute />}
            </ControlButton> */}
            {/* Test button for development */}
            <ControlButton 
              onClick={() => {
                // Simulate receiving a message for testing
                const testMessage = {
                  id: Date.now(),
                  user: { username: 'TestUser', id: 'test123' },
                  message: 'This is a test message from another user!',
                  createdAt: new Date().toISOString(),
                  type: 'user'
                };
                // Manually trigger the message handler
                setMessages(prev => [...prev, {
                  id: testMessage.id,
                  username: testMessage.user.username,
                  message: testMessage.message,
                  timestamp: new Date().toLocaleTimeString(),
                  isOwn: false,
                  isSystem: false
                }]);
                console.log('🧪 Test message added');
              }}
              title="Test message (Dev)"
            >
              🧪
            </ControlButton>
          </ChatControls>
        </ChatHeader>

        <ChatMessages>
          {isLoading ? (
            <SystemMessage>Loading chat history...</SystemMessage>
          ) : error ? (
            <SystemMessage style={{ color: theme.colors.error }}>⚠️ {error}</SystemMessage>
          ) : (
            <>
              {!isConnected && (
                <SystemMessage style={{ color: theme.colors.warning }}>
                  🔄 Connecting to real-time chat... Your messages will be sent once connected.
                </SystemMessage>
              )}
              {messages.map(renderMessage)}
            </>
          )}
          {typingUsers.length > 0 && (
            <TypingIndicator>
              {typingUsers.map(u => u.username).join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
            </TypingIndicator>
          )}
          <div ref={messagesEndRef} />
        </ChatMessages>

        <ChatInputContainer>
          <InputWrapper>
            <div ref={emojiPickerRef} style={{ position: 'relative' }}>
              <EmojiButton 
                className={showEmojiPicker ? 'active' : ''}
                onClick={toggleEmojiPicker}
                title="Add emoji"
              >
                <FaSmile />
              </EmojiButton>
              {showEmojiPicker && (
                <EmojiPicker>
                  {commonEmojis.map((emoji, index) => (
                    <EmojiItem
                      key={index}
                      onClick={() => handleEmojiSelect(emoji)}
                      title={`Add ${emoji}`}
                    >
                      {emoji}
                    </EmojiItem>
                  ))}
                </EmojiPicker>
              )}
            </div>
            <MessageInput
              value={newMessage}
              onChange={(e) => handleTyping(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isConnected ? "Type your message... (Press Enter to send)" : "Connecting to chat..."}
              disabled={!isConnected || isLoading}
              rows={1}
            />
            <SendButton 
              onClick={handleSendMessage}
              disabled={newMessage.trim() === ''}
              title="Send message"
            >
              <FaPaperPlane />
            </SendButton>
          </InputWrapper>
        </ChatInputContainer>
      </ChatContent>
    </ChatContainer>
  );
};

export default ChatRoom;